import React, { useEffect, useState } from 'react';
import {
  fetchLeadSources,
  downloadLeadSourcesCsv,
  mapBackendClientId,
  type LeadSources,
} from '../services/dashboardApi';

export interface LeadSourcesPanelTheme {
  cardBg: string;
  cardBorder: string;
  cardShadow: string;
  accent: string;
  sectionTitle: string;
  sectionDivider: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  rowDivider: string;
  positive: string;
  negative: string;
}

export interface LeadSourcesPanelProps {
  clientId: string;
  theme: LeadSourcesPanelTheme;
  period: 'week' | 'month';
  portfolioKey?: string;
  communityUnique?: string;
  /** PLC two-axis filter — used when clientId routes to /dashboard/plc/* */
  ownershipGroup?: string;
  communityGroup?: string;
  communityId?: number;
  referenceDate?: string;
  /** 'previous' (default — prior week/month) or 'yoy' (same period one year earlier). */
  compare?: 'previous' | 'yoy';
  /** How many sources to show in the panel (default 8). */
  limit?: number;
}

function fmt(n: number | null | undefined): string {
  return n != null ? n.toLocaleString() : '—';
}

function pct(n: number | null | undefined, signed = false): string {
  if (n == null) return '—';
  const v = (n * 100).toFixed(1);
  return signed && n > 0 ? `+${v}%` : `${v}%`;
}

export const LeadSourcesPanel: React.FC<LeadSourcesPanelProps> = ({
  clientId,
  theme,
  period,
  portfolioKey,
  communityUnique,
  ownershipGroup,
  communityGroup,
  communityId,
  referenceDate,
  compare = 'previous',
  limit = 8,
}) => {
  const [data, setData] = useState<LeadSources | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const backendId = mapBackendClientId(clientId);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchLeadSources(backendId, {
      period,
      portfolio: portfolioKey || undefined,
      communityUnique: communityUnique || undefined,
      ownershipGroup: ownershipGroup || undefined,
      communityGroup: communityGroup || undefined,
      communityId,
      referenceDate,
      compare,
      limit,
    })
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e: Error) => {
        if (!cancelled) { setError(e.message); setLoading(false); }
      });
    return () => { cancelled = true; };
  }, [backendId, period, portfolioKey, communityUnique, ownershipGroup, communityGroup, communityId, referenceDate, compare, limit]);

  const handleCsv = async () => {
    setDownloading(true);
    try {
      await downloadLeadSourcesCsv(backendId, {
        period,
        portfolio: portfolioKey || undefined,
        communityUnique: communityUnique || undefined,
        ownershipGroup: ownershipGroup || undefined,
        communityGroup: communityGroup || undefined,
        communityId,
        referenceDate,
        compare,
        limit: 50,
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDownloading(false);
    }
  };

  const periodLabel = period === 'week' ? 'This Week' : 'This Month';
  const priorLabel = compare === 'yoy'
    ? 'LY'
    : period === 'week' ? 'PW' : 'PM';
  const totalCurrent = data?.totals.leads_current ?? 0;
  const maxLeads = data?.sources.reduce((m, s) => Math.max(m, s.leads_current), 0) ?? 0;

  return (
    <div style={{
      background: theme.cardBg,
      border: `1px solid ${theme.cardBorder}`,
      boxShadow: theme.cardShadow,
      borderRadius: 4,
      padding: '1.25rem 1.5rem',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: '1rem',
        paddingBottom: '0.5rem',
        borderBottom: `1px solid ${theme.sectionDivider}`,
      }}>
        <h3 style={{
          fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
          textTransform: 'uppercase', color: theme.sectionTitle, margin: 0,
        }}>
          Lead Source Breakdown — {periodLabel}
        </h3>
        <button
          type="button"
          onClick={handleCsv}
          disabled={loading || downloading || !data || data.sources.length === 0}
          style={{
            fontSize: '0.7rem',
            fontWeight: 600,
            letterSpacing: '0.05em',
            color: theme.accent,
            background: 'transparent',
            border: `1px solid ${theme.accent}`,
            borderRadius: 3,
            padding: '0.25rem 0.75rem',
            cursor: loading || downloading || !data?.sources.length ? 'not-allowed' : 'pointer',
            opacity: loading || downloading || !data?.sources.length ? 0.5 : 1,
          }}
        >
          {downloading ? 'Downloading…' : 'CSV'}
        </button>
      </div>

      {loading && (
        <p style={{ color: theme.textMuted, fontSize: '0.85rem', margin: 0 }}>Loading…</p>
      )}
      {error && (
        <p style={{ color: theme.negative, fontSize: '0.85rem', margin: 0 }}>{error}</p>
      )}
      {!loading && !error && data && data.sources.length === 0 && (
        <p style={{ color: theme.textMuted, fontSize: '0.85rem', margin: 0 }}>
          No lead source data in this period.
        </p>
      )}
      {!loading && !error && data && data.sources.length > 0 && (
        <>
          <p style={{
            color: theme.textMuted,
            fontSize: '0.75rem',
            margin: '0 0 0.75rem',
          }}>
            {fmt(totalCurrent)} leads across {data.totals.unique_sources_current} sources
            {' '} (vs {fmt(data.totals.leads_prior)} {priorLabel})
          </p>
          <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {data.sources.map((s) => {
              const widthPct = maxLeads > 0 ? (s.leads_current / maxLeads) * 100 : 0;
              const deltaTone =
                s.delta > 0 ? theme.positive :
                s.delta < 0 ? theme.negative :
                theme.textMuted;
              const deltaStr = s.delta > 0 ? `+${s.delta}` : s.delta < 0 ? `${s.delta}` : '0';
              return (
                <li key={s.source} style={{
                  padding: '0.5rem 0',
                  borderBottom: `1px solid ${theme.rowDivider}`,
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: '0.3rem',
                    fontSize: '0.85rem',
                  }}>
                    <span style={{
                      color: theme.textPrimary,
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '60%',
                    }}>
                      {s.source}
                    </span>
                    <span style={{ color: theme.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                      <strong style={{ color: theme.textPrimary }}>{fmt(s.leads_current)}</strong>
                      {' '}
                      <span style={{ color: theme.textMuted, fontSize: '0.75rem' }}>
                        ({pct(s.share_current)})
                      </span>
                      {' · '}
                      <span style={{ color: deltaTone, fontSize: '0.75rem', fontWeight: 600 }}>
                        {deltaStr}
                        {s.delta_pct != null && ` (${pct(s.delta_pct, true)})`}
                      </span>
                    </span>
                  </div>
                  <div style={{
                    height: 4,
                    background: theme.rowDivider,
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${widthPct}%`,
                      height: '100%',
                      background: theme.accent,
                    }} />
                  </div>
                </li>
              );
            })}
          </ol>
        </>
      )}
    </div>
  );
};
